import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';
import { filter, map, skip, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'item-row',
  templateUrl: './item-row.component.html',
  styleUrls: ['./item-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemRowComponent implements OnInit, OnDestroy {
  @Input() item;
  @Input() index;
  @Input() index$ = new Subject<number[]>();
  @Input() fields = [];
  @Input() noSet;
  @Input() templates = {};
  @Input() collection;
  @Input() fieldsChanged;
  @Input() itemHeaderTemplate;
  @Input() itemFooterTemplate;

  @Output() submitClick = new EventEmitter();

  @ViewChild('itemTemplate', { static: false }) itemTemplate: TemplateRef<HTMLElement>;

  private _destroyed$ = new Subject();

  clickOnItem(event, field, item) {
    this.submitClick.next({
      event,
      field,
      item
    });
  }

  identifyField(index, item) {
    return item.key;
  }

  toggleItem(item): void {
    if (item) {
      this._renderer.removeClass(this._el.nativeElement, 'hidden');
    } else {
      this._renderer.addClass(this._el.nativeElement, 'hidden');
    }
  }

  fieldsSubscription(): void {
    if (this.fieldsChanged) {
      this.fieldsChanged
        .pipe(
          takeUntil(this._destroyed$),
          map((fields) => {
            if (fields instanceof Array) {
              this.fields = fields;
            }

            if (this.index < 10) {
              this._cdRef.markForCheck();
            } else {
              setTimeout(() => {
                this._cdRef.markForCheck();
              });
            }
          })
        )
        .subscribe();
    }
  }

  overrideRow(index): void {
    const newItem = this.collection.items[index];

    this.toggleItem(newItem);

    if (newItem) {
      this.item = newItem;

      this._el.nativeElement.className = '';

      if (this.item.itemClass) {
        this._renderer.addClass(this._el.nativeElement, this.item.itemClass);
      }

      if (this.index < 10) {
        this._cdRef.markForCheck();
      } else {
        setTimeout(() => {
          this._cdRef.markForCheck();
        });
      }
    }
  }

  private toggleSelectedItem(): void {
    // @ts-ignore
    const itemElement = this.itemTemplate?.nativeElement;
    if (!itemElement) {
      return;
    }

    if (this.collection.selectedItems.has(this.item?.id)) {
      this._renderer.addClass(itemElement, 'selected');
    } else {
      this._renderer.removeClass(itemElement, 'selected');
    }
  }

  collectionSubscription(): void {
    if (this.index$) {
      this.index$
        .pipe(
          takeUntil(this._destroyed$),
          map((ids) => {
            // console.log(this.index, ids[this.index]);
            this.overrideRow(ids[this.index]);
          })
        )
        .subscribe();
    }
    if (this.collection) {
      this.collection.onLoad$
        .pipe(
          takeUntil(this._destroyed$),
          filter(() => !this.collection.loading),
          map(() => this.overrideRow(this.index))
        )
        .subscribe();

      this.collection.selectedItems$
        .pipe(
          takeUntil(this._destroyed$),
          skip(2),
          map(() => this.toggleSelectedItem())
        )
        .subscribe();

      if (this.collection.items[this.index]) {
        this.overrideRow(this.index);
      }
    }
  }

  ngOnInit(): void {
    this._renderer.addClass(this._el.nativeElement, 'list-item');
    if (this.item?.itemClass) {
      this._renderer.addClass(this._el.nativeElement, this.item.itemClass);
    } else {
      let classes = this._el.nativeElement.getAttribute('class').split(' ');
      classes.forEach((cl) => {
        setTimeout(() => {
          this._renderer.removeClass(this._el.nativeElement, cl);
        });
      });
    }

    this.toggleItem(this.item);

    this.collectionSubscription();
    this.fieldsSubscription();
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  constructor(
    private _cdRef: ChangeDetectorRef,
    private _el: ElementRef,
    private _renderer: Renderer2
  ) {}
}
